import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { Delete, Done, WarningAmber } from "@mui/icons-material";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import LoadingButton from "@mui/lab/LoadingButton";
import { DeleteDialog } from "../../../../components/form/DeleteDialog";
import React, { useState } from "react";

type ProductSelectionRowProps = {
  selectedItems: GridRowSelectionModel;
  resetSelection: () => void;
};

export const ProductSelectionRow = (props: ProductSelectionRowProps) => {
  const { selectedItems, resetSelection } = props;
  const { translate } = useTranslation(
    "pages.inventoryManagement.inventory.approve",
  );
  const { translate: translateDelete } = useTranslation(
    "common.components.deleteList",
  );

  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const invalidate = useInvalidate();
  const { mutate, isLoading } = useCustomMutation();
  const [deleteDialog, setDeleteDialog] = useState<
    "all" | "selected" | undefined
  >();

  // ------------------------------
  // DANGER ZONE MENU STATE
  // ------------------------------
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dangerOpen = Boolean(anchorEl);

  const openDangerMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeDangerMenu = () => setAnchorEl(null);

  const handleApprove = (approveAll: boolean) => {
    mutate(
      {
        url:
          `store/${selectedCountry?.id}/product/approve` +
          (approveAll ? "/all" : ""),
        method: "post",
        values: approveAll
          ? {}
          : {
              asinList: selectedItems,
            },
        successNotification: () => {
          return {
            message: translate("success", { count: selectedItems?.length }),
            type: "success",
          };
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });
          resetSelection();
        },
      },
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        margin: "8px 0",
        padding: "8px 16px",
        backgroundColor: (theme) => theme.palette.customBackground.box,
        borderRadius: 2,
      }}
    >
      <Typography>
        {translate("selected", {
          count: selectedItems?.length ?? 0,
        })}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 1,
        }}
      >
        <LoadingButton
          loading={isLoading}
          variant="contained"
          color="success"
          startIcon={<Done />}
          onClick={() => handleApprove(false)}
        >
          {translate("approveSelected")}
        </LoadingButton>

        <LoadingButton
          disabled={isLoading}
          variant="contained"
          color="error"
          startIcon={<Delete />}
          onClick={() => setDeleteDialog("selected")}
        >
          {translateDelete("deleteSelected")}
        </LoadingButton>
      </Box>

      <Box flex={1} />

      <IconButton color="error" onClick={openDangerMenu}>
        <WarningAmber />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={dangerOpen}
        onClose={closeDangerMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            closeDangerMenu();
            handleApprove(true);
          }}
        >
          <ListItemText primary={translate("approveAll")} />
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeDangerMenu();
            setDeleteDialog("all");
          }}
        >
          <ListItemText primary={translateDelete("deleteAll")} />
        </MenuItem>
      </Menu>
      {/* ------------------------------ */}

      <DeleteDialog
        handleClose={() => setDeleteDialog(undefined)}
        dialogType={deleteDialog}
        endpoint={`store/${selectedCountry?.id}/product/delete`}
        resetSelection={resetSelection}
        values={{ asinList: selectedItems }}
      />
    </Box>
  );
};
